import React from 'react';
import { Slideshow, GAME_SLIDES, Pic } from './Landing';

// Asset art (added 26 Oct 2026). The land schema, 10 of 12 drawn so far.
// Deliberately described as game assets, not as collectibles — nothing here
// is on sale and the tokenisation layer is still awaiting UK legal sign-off,
// so no price, no rarity-as-scarcity language and no transfer claims.
// File names mirror urvara/assets/wax-art/01-land/ so the two stay in step.
const LAND_ASSETS = [
  { f: 'asset-001-agvlndbuyarb-LND-BUY-ARABLE.jpg', n: 'Arable land, purchase', t: 'epic' },
  { f: 'asset-002-agvlndbuygrz-LND-BUY-GRAZING.jpg', n: 'Rough grazing', t: 'rare' },
  { f: 'asset-003-agvlndrntarb-LND-RENT-ARABLE.jpg', n: 'Arable land, rented', t: 'uncommon' },
  { f: 'asset-004-agvlndrntplt-LND-RENT-PLOT.jpg', n: 'Small plot lease', t: 'common' },
  { f: 'asset-005-agvlnduntind-LND-UNIT-IND.jpg', n: 'Industrial unit', t: 'uncommon' },
  { f: 'asset-006-agvlndarchra-LND-ARCH.jpg', n: 'Railway arch', t: 'uncommon' },
  { f: 'asset-007-agvlndrofles-LND-ROOF-LEASE.jpg', n: 'Rooftop lease', t: 'uncommon' },
  { f: 'asset-008-agvlndcouncl-LND-COUNCIL.jpg', n: 'Council growing site', t: 'common' },
  { f: 'asset-009-agvlndmenwhl-LND-MEANWHILE.jpg', n: 'Meanwhile-use site', t: 'common' },
  { f: 'asset-010-agvlndsealcn-LND-SEA-LICENCE.jpg', n: 'Seabed lease', t: 'rare' },
];

// Game home (added 25 Oct 2026). Route: /game.
// Renamed 25 Oct: the game carries the AgriVoLED name, not a separate one.
// The landing page is the COMPANY home; this is the GAME home.
// Deliberately carries no pass prices, no prize schedule and no claim that
// produce can be won — those structures are still awaiting UK legal sign-off.

const PILLARS = [
  {
    title: 'Real trade-offs',
    body: 'Indoor farms yield more, use more energy and can never be certified organic. No-dig is slower and builds soil you still have in 2050. Every choice costs something.',
    colour: '#16a34a',
  },
  {
    title: 'Information is power',
    body: 'You begin blind: yields are wide ranges and problems surface too late. Sensors and records turn guesses into forecasts, and forecasts into contracts.',
    colour: '#0ea5e9',
  },
  {
    title: 'Grounded in real data',
    body: 'Crops, climate, trade and growing techniques come from the same research and the same model as the rest of this site. Simplified, but honest.',
    colour: '#9333ea',
  },
];

const LAYERS = [
  { n: '01', name: 'Plot', desc: 'A bed, a room, a rooftop. What to plant, spacing, rotation, inputs.' },
  { n: '02', name: 'Site', desc: 'A farm or a building. Layout, structures, staff, water and energy.' },
  { n: '03', name: 'City', desc: 'Rooftops, basements, arches, cafés. Which buildings to take, which partners to keep.' },
  { n: '04', name: 'Region', desc: 'A national network. Where to expand, what to process, how to move it.' },
  { n: '05', name: 'World', desc: 'Trade and climate. Which imports to replace, and what the weather does to that plan.' },
];

const FACTS = [
  { k: '2026 → 2050', v: 'One season per turn' },
  { k: '18', v: 'Growing systems modelled' },
  { k: '22', v: 'Yield factors' },
  { k: '271', v: 'Assets, each with a real price' },
];

export default function Game() {
  console.log('[Game] render');
  return (
    <div className="landing-root game-root">
      <header className="visuals-header game-header">
        <a href="/" className="visuals-back">&larr; AgriVoLED home</a>
        <span className="game-tag">In development</span>
        <h1>A Global Strategy Game</h1>
        <p className="game-lede">
          Build your own plant-based food company anywhere in the world — from one plot
          to a national network — on the same data model as the rest of this work.
        </p>
      </header>

      {/* GAME SLIDESHOW 25 Oct 2026 — separate from the company slideshow */}
      <section className="section game-hero">
        <Slideshow slides={GAME_SLIDES} label="GameSlideshow"/>
      </section>

      <section className="section game-facts">
        {FACTS.map(f => (
          <div key={f.k} className="game-fact">
            <strong>{f.k}</strong>
            <span>{f.v}</span>
          </div>
        ))}
      </section>

      <section className="section">
        <div className="section-header">
          <h2 className="section-title gradient-text-green">What kind of game it is</h2>
          <p className="section-sub">
            Turn-based, one season at a time, from 2026 to 2050. No time pressure, no twitch,
            no pay-to-win.
          </p>
        </div>
        <div className="game-pillars">
          {PILLARS.map(p => (
            <article key={p.title} className="game-pillar" style={{ ['--pillar' as any]: p.colour }}>
              <h3>{p.title}</h3>
              <p>{p.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="section-header">
          <h2 className="section-title gradient-text">Zoom from plot to planet</h2>
          <p className="section-sub">
            Five layers, and decisions on one change the others. A drought in the region dries
            the soil on your plot; a new factory raises the price of what that plot grows.
          </p>
        </div>
        <ol className="game-layers">
          {LAYERS.map(l => (
            <li key={l.n} className="game-layer">
              <span className="game-layer-n">{l.n}</span>
              <div>
                <strong>{l.name}</strong>
                <span>{l.desc}</span>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ASSET GALLERY 26 Oct 2026 — the land schema */}
      <section className="section game-assets">
        <div className="section-header">
          <h2 className="section-title gradient-text-green">Every place you can grow</h2>
          <p className="section-sub">
            Land is the first of eleven asset families. Each one is a real place with real
            trade-offs &mdash; bought ground you keep, rented ground you do not, a railway arch,
            a rooftop, a council plot, a stretch of seabed. Ten of the twelve are drawn.
          </p>
        </div>
        <div className="game-asset-grid">
          {LAND_ASSETS.map(a => (
            <figure key={a.f} className={`game-asset ga-${a.t}`}>
              <Pic src={`/images/${a.f}`} alt={a.n} width={1024} height={1024}
                   sizes="(max-width: 700px) 45vw, 240px"/>
              <figcaption>
                <strong>{a.n}</strong>
                <span className={`ga-tier ga-tier-${a.t}`}>{a.t}</span>
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="game-asset-note">
          271 assets are specified across land, structures, tools, machinery, planting stock,
          inputs, energy, post-harvest, sensing, logistics and the intangibles. Artwork is being
          drawn in that order. <strong>Nothing shown here is on sale.</strong>
        </p>
      </section>

      <section className="section game-score">
        <div className="section-header">
          <h2 className="section-title gradient-text-warm">Scored three ways at once</h2>
          <p className="section-sub">
            Your weakest of the three counts double at the end, so none of them can be ignored.
          </p>
        </div>
        <div className="game-score-row">
          <div className="game-score-card" style={{ ['--sc' as any]: '#f59e0b' }}>
            <strong>Profit</strong><span>Company value, cash flow, cost per kilogram.</span>
          </div>
          <div className="game-score-card" style={{ ['--sc' as any]: '#16a34a' }}>
            <strong>Planet</strong><span>Soil organic matter, carbon, water, biodiversity, energy source.</span>
          </div>
          <div className="game-score-card" style={{ ['--sc' as any]: '#0ea5e9' }}>
            <strong>People</strong><span>Imports replaced, jobs at a real living wage, protein supplied.</span>
          </div>
        </div>
      </section>

      <section className="section game-status">
        <div className="game-status-box">
          <h2>Where it is now</h2>
          <p>
            A first playable season is live. Crop calendars, labour seasonality, soil, seasonal
            prices and market saturation all run from the same model as the research on this site.
            It is an early slice rather than the finished game &mdash; one holding, one season at a
            time &mdash; and <strong>nothing here is on sale</strong>.
          </p>
          <p className="game-status-note">
            The game shares one asset model with the growing systems described on this site, so the
            same research drives both. Classroom use is planned and will always be free.
          </p>
          <a href="/game/play" className="game-cta-btn">
            <span className="game-cta-btn-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                <path d="M8 5.5v13a1 1 0 0 0 1.52.85l10.5-6.5a1 1 0 0 0 0-1.7L9.52 4.65A1 1 0 0 0 8 5.5z"/>
              </svg>
            </span>
            Start a season
          </a>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-spectrum"/>
        <div className="footer-inner">
          <span className="footer-name">Agri<span>VoLED</span></span>
          <div className="footer-links">
            <a href="/visuals">Visuals</a><a href="/farm">Farm</a><a href="/">Home</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
